import type {
  PublicActionItem,
  PublicActionItemEvent,
} from '@/backend/contracts/action-items-client.ts';

import type {
  ActionItemAssignment,
  ActionItemAssignmentStatus,
  ActionItemEvent,
  ActionItemEventKind,
  ActionItemEventMetadata,
} from './types';

const ASSIGNMENT_ID_SEPARATOR = '_';

export function actionItemEventId(operationId: string, role: string): string {
  const normalizedRole = role.trim();
  if (!operationId.trim() || !normalizedRole) {
    throw new Error('Action Item event operationId and role are required');
  }
  return `${encodeURIComponent(operationId)}__${encodeURIComponent(normalizedRole)}`;
}

function requiredIdPart(value: string, label: 'actionItemId' | 'personnelId'): string {
  const trimmed = value.trim();
  if (!trimmed) {
    throw new Error(`Action Item assignment ${label} is required`);
  }
  return trimmed;
}

function encodeAssignmentIdPart(value: string, label: 'actionItemId' | 'personnelId'): string {
  return encodeURIComponent(requiredIdPart(value, label)).replaceAll(ASSIGNMENT_ID_SEPARATOR, '%5F');
}

function requiredEventString(
  value: string,
  label: 'operationId' | 'actionItemId' | 'summary',
): string {
  const trimmed = value.trim();
  if (!trimmed) {
    throw new Error(`Action Item event ${label} is required`);
  }
  return trimmed;
}

export function actionItemAssignmentId(actionItemId: string, personnelId: string): string {
  return [
    encodeAssignmentIdPart(actionItemId, 'actionItemId'),
    encodeAssignmentIdPart(personnelId, 'personnelId'),
  ].join(ASSIGNMENT_ID_SEPARATOR);
}

export interface CreateActionItemAssignmentInput {
  actionItemId: string;
  personnelId: string;
  status?: ActionItemAssignmentStatus;
  nowIso?: string;
  actorId?: string;
}

export function createActionItemAssignment(input: CreateActionItemAssignmentInput): ActionItemAssignment {
  const now = input.nowIso ?? new Date().toISOString();
  const status = input.status ?? 'assigned';
  const actionItemId = requiredIdPart(input.actionItemId, 'actionItemId');
  const personnelId = requiredIdPart(input.personnelId, 'personnelId');

  return {
    id: actionItemAssignmentId(actionItemId, personnelId),
    actionItemId,
    personnelId,
    status,
    createdAt: now,
    ...(input.actorId ? { createdBy: input.actorId } : {}),
    updatedAt: now,
    ...(input.actorId ? { updatedBy: input.actorId } : {}),
    statusUpdatedAt: now,
    ...(input.actorId ? { statusUpdatedBy: input.actorId } : {}),
  };
}

export function isVisibleActionItemAssignment(assignment: ActionItemAssignment): boolean {
  return assignment.status !== 'removed';
}

export function selectVisibleActionItemAssignments(
  assignments: readonly ActionItemAssignment[],
  actionItemId?: string,
): ActionItemAssignment[] {
  return assignments
    .filter(assignment => (
      isVisibleActionItemAssignment(assignment) &&
      (actionItemId === undefined || assignment.actionItemId === actionItemId)
    ))
    .toSorted((left, right) => right.updatedAt.localeCompare(left.updatedAt));
}

export function selectActionItemsAssignedToPersonnel(input: {
  items: readonly PublicActionItem[];
  assignments: readonly ActionItemAssignment[];
  personnelId: string | null | undefined;
  includeClosed?: boolean;
}): PublicActionItem[] {
  if (!input.personnelId) {
    return [];
  }

  const assignedItemIds = new Set(
    input.assignments
      .filter(assignment => (
        assignment.personnelId === input.personnelId &&
        isVisibleActionItemAssignment(assignment)
      ))
      .map(assignment => assignment.actionItemId),
  );

  return input.items
    .filter(item => (
      assignedItemIds.has(item.id) &&
      (input.includeClosed === true || item.lifecycle !== 'closed')
    ))
    .toSorted((left, right) => right.updatedAt.localeCompare(left.updatedAt));
}

export interface CreateActionItemEventInput {
  operationId: string;
  actionItemId: string;
  kind: ActionItemEventKind;
  summary: string;
  occurredAt?: string;
  actorId?: string;
  assignmentId?: string;
  personnelId?: string;
  fromStatus?: ActionItemAssignmentStatus;
  toStatus?: ActionItemAssignmentStatus;
  metadata?: ActionItemEventMetadata;
}

export function createActionItemEvent(input: CreateActionItemEventInput): ActionItemEvent {
  const operationId = requiredEventString(input.operationId, 'operationId');
  const actionItemId = requiredEventString(input.actionItemId, 'actionItemId');
  const summary = requiredEventString(input.summary, 'summary');

  return {
    id: actionItemEventId(operationId, input.kind),
    operationId,
    actionItemId,
    kind: input.kind,
    occurredAt: input.occurredAt ?? new Date().toISOString(),
    ...(input.actorId ? { actorId: input.actorId } : {}),
    ...(input.assignmentId ? { assignmentId: input.assignmentId } : {}),
    ...(input.personnelId ? { personnelId: input.personnelId } : {}),
    ...(input.fromStatus ? { fromStatus: input.fromStatus } : {}),
    ...(input.toStatus ? { toStatus: input.toStatus } : {}),
    summary,
    ...(input.metadata ? { metadata: { ...input.metadata } } : {}),
  };
}

export function projectPublicActionItemEvent(event: ActionItemEvent): PublicActionItemEvent {
  return {
    id: event.id,
    actionItemId: event.actionItemId,
    kind: event.kind,
    occurredAt: event.occurredAt,
    ...(event.actorId ? { actorId: event.actorId } : {}),
    ...(event.assignmentId ? { assignmentId: event.assignmentId } : {}),
    ...(event.personnelId ? { personnelId: event.personnelId } : {}),
    ...(event.fromStatus ? { fromStatus: event.fromStatus } : {}),
    ...(event.toStatus ? { toStatus: event.toStatus } : {}),
    summary: event.summary,
  };
}
